import React, { useRef, useState, useEffect } from "react";
import { DigitalShiplitBottomSheetProps, ShiplitStatus, DigitalShiplitPageContext } from "../component.d";
import fetchDocument, {
    FetchReportError,
    processDocumentResponse,
    downloadTimeout,
    handleDownloadClick,
    StatementStatus
} from "../common/utils/DocumentFetchUtils";
import "./DigitalShiplit.scss";

const DIGITAL_SHIPLIT_URL = '/your-orders-api/command/digital-shiplit';

/**
 * Bottom sheet for downloading digital shipment literature PDFs.
 * Handles cross-platform downloads (web, iOS, Android) with proper error handling.
 * 
 * @param props.bottomSheetRef - Ref to control the bottom sheet
 * @param props.labels - UI text labels for different states
 * @param props.iosDevice - Whether running on iOS app
 * @param props.androidDevice - Whether running on Android app  
 * @param props.mShop - Whether running in mobile app context
 */
function DigitalShiplitBottomSheet(props: DigitalShiplitBottomSheetProps) {
    const bottomSheetRef = useRef<any>(null);
    const [shiplitStatus, setShiplitStatus] = useState<ShiplitStatus>(ShiplitStatus.Hidden);
    const [currentOrderKeyId, setCurrentOrderKeyId] = useState<string>("");
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [downloadError, setDownloadError] = useState<FetchReportError>();

    // We have hardcoded the name here but we never expcect to not have the prop. (satisfying TS from throwing compile errors)
    const fileName = props.labels.pdfNameShipmentLiterature ? `${props.labels.pdfNameShipmentLiterature}.pdf` : `Amazon Pharmacy-Med Information.pdf`; 
    let currentPage: string;
    if (props.currentPage === DigitalShiplitPageContext.OrderTracker) {
        currentPage = DigitalShiplitPageContext.OrderTracker;
    } else if (props.currentPage === DigitalShiplitPageContext.OrderSummary) {
        currentPage = DigitalShiplitPageContext.OrderSummary;
    } else {
        currentPage = DigitalShiplitPageContext.DirectAccess;
    }

    const showBottomSheet = (orderKeyId: string) => {
        setCurrentOrderKeyId(orderKeyId);
        setShiplitStatus(ShiplitStatus.Downloading);
        bottomSheetRef?.current?.show();

        downloadShiplit(orderKeyId);
    };

    const downloadShiplit = (orderKeyId: string) => {
        const fetchShiplit = async (filterOptionResult: any): Promise<Blob> => {
            const controller = new AbortController();
            setAbortController(controller);
            
            setTimeout(() => {
                controller.abort();
            }, downloadTimeout);

            // Fetch the document
            const shiplitPromise = fetchDocument(
                `${DIGITAL_SHIPLIT_URL}?orderKeyId=${orderKeyId}&currentPage=${currentPage}`,
                undefined,
                { signal: controller.signal }
            );

            return processDocumentResponse(
                shiplitPromise,
                {
                    filterOptionValue: orderKeyId,
                    noStatementErrorMessage: props.labels.downloadingErrorShipmentLiterature || "", // This is an empty string for TypeScript compile errors, we will NEVER expect the prop to be undefined
                    fileName: fileName || "",  // This is an empty string for TypeScript compile errors, we will NEVER expect the prop to be undefined
                    mShop: props.isMShop,
                    iosDevice: props.isIosDevice,
                    androidDevice: props.isAndroidDevice,
                    downloadComplete: () => setShiplitStatus(ShiplitStatus.Done)
                },
                { reason: undefined }
            );
        };

        handleDownloadClick(
            { orderKeyId }, // Pass any object as filterOptionResult
            fetchShiplit,
            fileName,
            (status: any) => {
                if (status === StatementStatus.Done) {
                    setShiplitStatus(ShiplitStatus.Done);
                } else if (status === StatementStatus.Downloading) {
                    setShiplitStatus(ShiplitStatus.Downloading);
                } else if (status === StatementStatus.GenerationError || status === StatementStatus.NotFoundError) {
                    setShiplitStatus(ShiplitStatus.Error);
                }
                setAbortController(null);
            },
            (error: FetchReportError) => {
                setDownloadError(error);
                if (error.name !== "hide") {
                    setShiplitStatus(ShiplitStatus.Error);
                }
                setAbortController(null);
            },
            props.isIosDevice,
            props.isMShop
        );
    };

    const closeBottomSheet = () => {
        if (abortController && shiplitStatus === ShiplitStatus.Downloading) {
            abortController.abort();
        }

        bottomSheetRef?.current?.hide();

        setShiplitStatus(ShiplitStatus.Hidden);
        setCurrentOrderKeyId("");
        setAbortController(null);
        setDownloadError(undefined);
    };

    const handleHideBottomSheet = () => {
        if (abortController && shiplitStatus === ShiplitStatus.Downloading) {
            abortController.abort();
        }

        setShiplitStatus(ShiplitStatus.Hidden);
        setCurrentOrderKeyId("");
        setAbortController(null);
        setDownloadError(undefined);
    };

    useEffect(() => {
        const current = bottomSheetRef.current;
        if (current) {
            current.addEventListener('hideBottomSheet', handleHideBottomSheet);

            return () => {
                current.removeEventListener('hideBottomSheet', handleHideBottomSheet);
            };
        }
    });

    const generateSection = (status: ShiplitStatus) => {
        return (
            <>
                {status === ShiplitStatus.Downloading &&
                    <pui-section direction="vertical" >
                        <pui-section-column spacing="large" style={{ height: "24px" }}>
                            <pui-loading-indicator style={{ position: 'relative' }} />
                        </pui-section-column>
                        <pui-section-column overflow="true">
                            <pui-text spacingBottom="medium" input={props.labels.downloadingShipmentLiterature} />
                        </pui-section-column>
                    </pui-section>
                }
                {status === ShiplitStatus.Done && <>
                    <pui-section flowdirection="vertical" >
                        <pui-section-column spacing="large">
                            <div className="pui-icon status-success-icon" style={{ display: "flex", justifyContent: "center", height: "32px", width: "32px" }} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-text spacingBottom="medium" input={props.labels.downloadingDoneShipmentLiterature} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-button
                                id="button-to-confirm-shiplit-download"
                                data-csa-c-type="button"
                                data-csa-c-action="confirm"
                                data-csa-c-content-id="button-to-confirm-shiplit-download"
                                data-csa-c-slot-id="digital-shiplit"
                                label={props.labels.continueShipmentLiterature}
                                spacingTop="small"
                                textColor="black-color"
                                textSize="extra-large"
                                onClick={closeBottomSheet}
                            />
                        </pui-section-column>
                    </pui-section>
                </>
                }
                {status === ShiplitStatus.Error && <>
                    <pui-section flowdirection="vertical" >
                        <pui-section-column spacing="large">
                            <div className="pui-icon status-error-icon" style={{ display: "flex", justifyContent: "center", height: "32px", width: "32px" }} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-text spacingBottom="medium" input={props.labels.downloadingErrorShipmentLiterature} />
                        </pui-section-column>
                        <pui-section-column>
                            <pui-button
                                id="button-to-confirm-shiplit-error"
                                data-csa-c-type="button"
                                data-csa-c-action="confirm"
                                data-csa-c-content-id="button-to-confirm-shiplit-error"
                                data-csa-c-slot-id="digital-shiplit"
                                label={props.labels.continueShipmentLiterature}
                                spacingTop="small"
                                textColor="black-color"
                                textSize="extra-large"
                                onClick={closeBottomSheet}
                            />
                        </pui-section-column>
                    </pui-section>
                </>
                }
            </>
        );
    };

    // Expose the showBottomSheet method to the parent component
    React.useImperativeHandle(props.bottomSheetRef, () => ({
        showBottomSheet
    }));

    return (
        <pui-bottom-sheet
            disableModalPadding
            enablescrollbar
            id="digital-shiplit-modal"
            data-csa-c-content-id="digital-shiplit-modal"
            data-csa-c-type="popup"
            data-csa-c-slot-id="digital-shiplit"
            ref={bottomSheetRef}
        >
            <div className="digital-shiplit__modal">
                <pui-section>
                    <pui-heading
                        input={props.labels.medicationInformationShipmentLiterature}
                        textSize="medium"
                        style={{ marginBottom: "4px" }}
                    />
                    {generateSection(shiplitStatus)}
                </pui-section>
            </div>
        </pui-bottom-sheet>
    );
}



export default DigitalShiplitBottomSheet;
